/**
 * Merchant memory — last-50 merchants the user has typed, with the
 * category they most often pair with and a running average amount.
 *
 * Storage shape: Record<lowercaseMerchant, MerchantMemoryEntry>
 * Storage key:   'margin.merchantMemory.v1'
 *
 * This file owns its own localStorage; it does NOT touch AppData or
 * bump DATA_VERSION. All localStorage access is wrapped in try/catch
 * so SSR / disabled storage / quota errors never throw to the caller.
 */

export interface MerchantMemoryEntry {
  /** Original merchant string most recently typed by the user. */
  merchant: string;
  /** Lowercased canonical key (also used as the map key). */
  lower: string;
  /** Number of transactions seen for this merchant. */
  count: number;
  /** Most recent amount entered. */
  lastAmount: number;
  /** Running average of every amount seen for this merchant. */
  avgAmount: number;
  /** Most-used categoryId for this merchant; null when never set. */
  topCategoryId: string | null;
  /** ISO timestamp of the last record. Drives LRU eviction. */
  lastUsedAt: string;
}

type Memory = Record<string, MerchantMemoryEntry>;

const STORAGE_KEY = 'margin.merchantMemory.v1';
const MAX_ENTRIES = 50;

const norm = (s: string): string => s.trim().toLowerCase();

function loadMemory(): Memory {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Memory;
    }
    return {};
  } catch {
    return {};
  }
}

function saveMemory(memory: Memory): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    /* quota / unavailable — non-fatal */
  }
}

/**
 * Cap memory at MAX_ENTRIES, evicting least-recently-used entries.
 * Mutates and returns the same map for chaining.
 */
function evictLRU(memory: Memory): Memory {
  const keys = Object.keys(memory);
  if (keys.length <= MAX_ENTRIES) return memory;
  const sorted = keys
    .map((k) => ({ k, t: memory[k]!.lastUsedAt }))
    .sort((a, b) => b.t.localeCompare(a.t));
  const toKeep = new Set(sorted.slice(0, MAX_ENTRIES).map((s) => s.k));
  for (const k of keys) {
    if (!toKeep.has(k)) delete memory[k];
  }
  return memory;
}

/**
 * Record a transaction in merchant memory. Updates count, last amount,
 * running average, and bumps the topCategoryId (when given). Idempotent
 * across reloads — every call persists.
 */
export function recordMerchant(
  merchant: string,
  amount: number,
  categoryId: string | null,
): void {
  try {
    const trimmed = merchant.trim();
    if (!trimmed) return;
    const lower = norm(trimmed);
    const memory = loadMemory();
    const prev = memory[lower];
    const now = new Date().toISOString();

    if (prev) {
      const nextCount = prev.count + 1;
      memory[lower] = {
        merchant: trimmed,
        lower,
        count: nextCount,
        lastAmount: amount,
        avgAmount:
          Math.round(((prev.avgAmount * prev.count + amount) / nextCount) * 100) /
          100,
        topCategoryId: categoryId ?? prev.topCategoryId,
        lastUsedAt: now,
      };
    } else {
      memory[lower] = {
        merchant: trimmed,
        lower,
        count: 1,
        lastAmount: amount,
        avgAmount: amount,
        topCategoryId: categoryId ?? null,
        lastUsedAt: now,
      };
    }

    evictLRU(memory);
    saveMemory(memory);
  } catch {
    /* swallow — never block a transaction save */
  }
}

/**
 * Return up to `limit` matching memory entries for `query`. Prefix
 * matches rank ahead of substring matches; ties broken by `count`
 * descending. Empty query returns the most-used N entries.
 */
export function suggestMerchants(
  query: string,
  limit = 5,
): MerchantMemoryEntry[] {
  try {
    const memory = loadMemory();
    const q = norm(query);
    const all = Object.values(memory);
    if (!q) {
      return [...all]
        .sort(
          (a, b) =>
            b.count - a.count || b.lastUsedAt.localeCompare(a.lastUsedAt),
        )
        .slice(0, limit);
    }
    const prefix: MerchantMemoryEntry[] = [];
    const contains: MerchantMemoryEntry[] = [];
    for (const e of all) {
      if (e.lower.startsWith(q)) prefix.push(e);
      else if (e.lower.includes(q)) contains.push(e);
    }
    const sortByCount = (a: MerchantMemoryEntry, b: MerchantMemoryEntry) =>
      b.count - a.count || b.lastUsedAt.localeCompare(a.lastUsedAt);
    prefix.sort(sortByCount);
    contains.sort(sortByCount);
    return [...prefix, ...contains].slice(0, limit);
  } catch {
    return [];
  }
}

/**
 * Exact lowercase lookup for a typed merchant string. Returns the
 * entry when found, otherwise null. Used to pull a remembered
 * category/amount when the user types a known merchant verbatim.
 */
export function getMerchantSuggestion(
  merchant: string,
): MerchantMemoryEntry | null {
  try {
    const lower = norm(merchant);
    if (!lower) return null;
    const memory = loadMemory();
    return memory[lower] ?? null;
  } catch {
    return null;
  }
}
